
import { memo } from 'react';
import { Handle, Position, NodeResizer } from '@xyflow/react';
import { Repeat, Settings, ChevronRight, Play, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNodeSettings } from '../NodeSettingsContext';
import { type MapContainerNodeData, type StateNodeData } from '@/types/workflow-graph';
import { StateBadges } from '../StateBadges';
import { cn } from '@/lib/utils';

interface MapContainerNodeProps {
    data: MapContainerNodeData;
    selected?: boolean;
}

/**
 * Map Container Node - A container that holds iterator states as nested children.
 * 
 * This represents an ASL Map state visually as a container with:
 * - Header: Shows state name, ItemsPath, MaxConcurrency, and feature badges
 * - Content area: Where child nodes (iterator states) are rendered by ReactFlow
 * - Handles: Input (left) and output (right) for workflow connections
 * 
 * The child nodes are positioned relative to this container using ReactFlow's
 * parentNode and extent properties.
 */
export const MapContainerNode = memo(function MapContainerNode({ 
    data, 
    selected 
}: MapContainerNodeProps) {
    const { openSettings } = useNodeSettings();
    const { 
        label, 
        state, 
        itemsPath, 
        maxConcurrency,
        isStart,
        isEnd,
        layoutDirection = 'LR',
    } = data;

    // Create a StateNodeData-compatible object for opening settings
    const settingsData: StateNodeData = {
        nodeType: 'state',
        label,
        stateType: 'Map',
        state,
        badges: data.badges,
        isStart,
        isEnd,
    };

    // Header height for content offset calculation
    const HEADER_HEIGHT = 52;

    // Determine handle positions based on layout direction
    const isHorizontal = layoutDirection === 'LR';
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    const outputPosition = isHorizontal ? Position.Right : Position.Bottom;
    
    // Handle positioning - centered on the side of the container, placed outside the border
    // For horizontal: center vertically with handle overlapping the edge
    // For vertical: center horizontally with handle overlapping the edge
    const inputStyle: React.CSSProperties = isHorizontal 
        ? { top: '50%', left: -8, transform: 'translateY(-50%)' }
        : { left: '50%', top: -8, transform: 'translateX(-50%)' };
    const outputStyle: React.CSSProperties = isHorizontal 
        ? { top: '50%', right: -8, transform: 'translateY(-50%)' }
        : { left: '50%', bottom: -8, transform: 'translateX(-50%)' };

    return (
        <div
            className={cn(
                'rounded-xl border-2 bg-emerald-50/80 backdrop-blur-sm',
                'shadow-lg',
                selected 
                    ? 'border-emerald-500 ring-2 ring-emerald-400 ring-offset-2' 
                    : 'border-emerald-300'
            )}
            style={{
                // Use 100% to fill the node dimensions set by React Flow
                // React Flow manages actual width/height via node.width and node.height
                width: '100%',
                height: '100%',
                minWidth: 400,
                minHeight: 200,
            }}
        >
            {/* Node Resizer for manual adjustment */}
            <NodeResizer
                minWidth={400}
                minHeight={200}
                isVisible={selected}
                lineClassName="!border-emerald-400"
                handleClassName="!h-3 !w-3 !border-2 !border-emerald-500 !bg-white"
            />

            {/* Input Handle - position based on layout direction */}
            {/* Always show input handle - even for start nodes (StartMarker connects to them) */}
            <Handle
                type="target"
                id="target"
                position={inputPosition}
                className="!h-4 !w-4 !border-2 !border-emerald-600 !bg-emerald-400"
                style={inputStyle}
            />

            {/* Header Section */}
            <div className="flex items-center gap-3 rounded-t-xl border-b-2 border-emerald-200 bg-gradient-to-r from-emerald-100 to-emerald-50 px-4 py-2.5">
                {/* Icon */}
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-200">
                    <Repeat className="h-5 w-5 text-emerald-700" />
                </div>

                {/* Title + Type + Description */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-900 truncate">
                            {label}
                        </span>
                        <span className="shrink-0 rounded-full bg-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                            Map
                        </span>
                        {/* Description (Comment) - to the right of title */}
                        {state.Comment && (
                            <span className="text-xs text-emerald-700/80 truncate" title={state.Comment}>
                                — {state.Comment.replace(/^Fan-out:\s*/i, '')}
                            </span>
                        )}
                    </div>
                    {/* Items Path */}
                    {itemsPath && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-emerald-700">
                            <ChevronRight className="h-3 w-3" />
                            <code className="rounded bg-emerald-200/50 px-1 font-mono text-[10px]">
                                {itemsPath}
                            </code>
                        </div>
                    )}
                </div>

                {/* Right side: MaxConcurrency + Settings */}
                <div className="flex items-center gap-2">
                    {maxConcurrency && (
                        <span className="rounded-full bg-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            ×{maxConcurrency}
                        </span>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-full text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800"
                        title="Map Settings"
                        onClick={(e) => {
                            e.stopPropagation();
                            openSettings(settingsData);
                        }}
                    >
                        <Settings size={14} />
                    </Button>
                </div>
            </div>

            {/* Content Area - where child nodes are rendered */}
            <div 
                className="relative overflow-visible"
                style={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}
            >
                {/* Iterator label - positioned in top-left of content */}
                <div className="absolute left-3 top-2 flex items-center gap-1.5 text-xs text-emerald-600">
                    <Play className="h-3 w-3" />
                    <span className="font-medium">Iterator</span>
                </div>

                {/* Visual indicator for empty iterator */}
                {(!data.iterator?.States || Object.keys(data.iterator.States).length === 0) && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-emerald-400">
                            <Repeat className="mx-auto h-8 w-8 opacity-50" />
                            <p className="mt-2 text-sm">Drop states here to build iterator</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Feature Badges (bottom-left) */}
            {data.badges && data.badges.length > 0 && (
                <div className="absolute bottom-2 left-14">
                    <StateBadges state={state} />
                </div>
            )}

            {/* Output Handle - position based on layout direction */}
            <Handle
                type="source"
                id="source"
                position={outputPosition}
                className="!h-4 !w-4 !border-2 !border-emerald-600 !bg-emerald-400"
                style={outputStyle}
            />

            {/* Container indicator - subtle pattern in background */}
            <div className="pointer-events-none absolute inset-0 rounded-xl opacity-[0.03]" 
                style={{
                    backgroundImage: `radial-gradient(circle, #059669 1px, transparent 1px)`,
                    backgroundSize: '20px 20px',
                }}
            />
        </div>
    );
});

